// Example sentences of the Dhātu lab and the person × number grid: the subject pronoun from the engine's pronoun
// tables plus the verb form (अहं पढामि।), and a Hindi / English meaning built from the root's dictionary meaning.
// Meanings are given for the present, the future, the command and for अस; other tables get a plain description.

import type { FormRef } from '@/prakrit/lib/grammar/formref'
import type {
    Lang,
    Num,
    Person,
    Tense
} from '@/prakrit/lib/grammar/types'
import type { LabRoot } from '@/prakrit/components/grammar/labs/verb-data'

import { P, SUBJ, resolveRef } from '@/prakrit/lib/grammar/formref'
import { TENSE_LABEL } from '@/prakrit/lib/grammar/labels'

export type SubjectGender = 'pu' | 'stri' | 'napum'

// ---------------------------------------------------------------- the subject pronoun (प्रथमा, from the engine)

const subjectRef = (p: Person, n: Num, g: SubjectGender): FormRef => {
    if (p !== 'anya') return SUBJ(p, n)
    if (g === 'napum') return P('ta-napum', 'prathama', n)

    return SUBJ(p, n, g)
}

/** अहं, अम्हे, तुमं, तुम्हे, and for अन्य पुरुष सो / ते, सा / ताओ or तं / ताणि. */
export const subjectOf = (p: Person, n: Num, g: SubjectGender = 'pu'): string => resolveRef(subjectRef(p, n, g)).display

/** The first core form of a cell, or ''. */
export const coreForm = (root: LabRoot, tense: Tense, p: Person, n: Num): string => (
    root.tables[tense]?.[p]?.[n]?.find(f => f.status === 'core')?.form ?? ''
)

/** "अहं पढामि।" — empty when the cell has no form. */
export const sentenceOf = (root: LabRoot, tense: Tense, p: Person, n: Num, g: SubjectGender = 'pu', form?: string): string => {
    const verb = form ?? coreForm(root, tense, p, n)

    return verb ? `${subjectOf(p, n, g)} ${verb}।` : ''
}

// ---------------------------------------------------------------- Hindi and English

const HI_SUBJECT: Record<Person, Record<Num, string>> = {
    uttam: { eka: 'मैं', bahu: 'हम' },
    madhyam: { eka: 'तुम', bahu: 'तुम सब' },
    anya: { eka: 'वह', bahu: 'वे' }
}

const EN_BE: Record<Person, Record<Num, string>> = {
    uttam: { eka: 'am', bahu: 'are' },
    madhyam: { eka: 'are', bahu: 'are' },
    anya: { eka: 'is', bahu: 'are' }
}

const enSubject = (p: Person, n: Num, g: SubjectGender): string => {
    if (p === 'uttam') return n === 'eka' ? 'I' : 'we'
    if (p === 'madhyam') return n === 'eka' ? 'you' : 'you all'
    if (n === 'bahu') return 'they'
    if (g === 'stri') return 'she'
    if (g === 'napum') return 'it'

    return 'he'
}

const enObject = (n: Num, g: SubjectGender): string => {
    if (n === 'bahu') return 'them'
    if (g === 'stri') return 'her'
    if (g === 'napum') return 'it'

    return 'him'
}

const capitalise = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1)

const DANGLING = new Set(['for', 'to', 'with', 'at', 'on', 'in', 'of', 'from'])

const thirdPerson = (verb: string): string => {
    if (verb === 'do') return 'does'
    if (verb === 'go') return 'goes'
    if (verb === 'have') return 'has'
    if (/(s|sh|ch|x|z|o)$/.test(verb)) return verb + 'es'
    if (/[^aeiou]y$/.test(verb)) return verb.slice(0, -1) + 'ies'

    return verb + 's'
}

type StemKind = 'consonant' | 'aa'

/** पढ़ (ends in a consonant) or जा / आ (ends in आ); other Hindi stems (पी, सो, दे) are not inflected here. */
const stemKind = (stem: string): StemKind | null => {
    if (stem.endsWith('ा') || /(^|\s)आ$/.test(stem)) return 'aa'
    if (/[क-हक़-य़़]$/.test(stem)) return 'consonant'

    return null
}

interface ParsedMeaning {
    /** The Hindi infinitive without -ना: पढ़, इच्छा कर, जा. */
    stem: string
    kind: StemKind | null
    /** The English verb and the words after it: "be" + " shy", "come" + " out". */
    verb: string
    tail: string
}

const parse = (root: LabRoot): ParsedMeaning | null => {
    const hi = root.hi.trim()
    const en = root.en.trim()

    if (en === 'to be' || !hi.endsWith('ना') || /[,/;(]/.test(hi) || !en.startsWith('to ') || /[,/;(]/.test(en)) return null

    const words = en.slice(3).split(/\s+/).filter(Boolean)

    if (words.length === 0 || DANGLING.has(words[words.length - 1])) return null

    const [verb, ...rest] = words
    const stem = hi.slice(0, -2)

    return { stem, kind: stemKind(stem), verb, tail: rest.length > 0 ? ` ${rest.join(' ')}` : '' }
}

const hiPresent = (p: Person, n: Num, fem: boolean): string => {
    if (p === 'uttam') return n === 'eka' ? 'ता हूँ' : 'ते हैं'
    if (p === 'madhyam') return 'ते हो'
    if (n === 'eka') return fem ? 'ती है' : 'ता है'

    return fem ? 'ती हैं' : 'ते हैं'
}

const hiFuture = (kind: StemKind, p: Person, n: Num, fem: boolean): string => {
    const c = kind === 'consonant'

    if (p === 'uttam') return n === 'eka' ? (c ? 'ूँगा' : 'ऊँगा') : (c ? 'ेंगे' : 'एँगे')
    if (p === 'madhyam') return c ? 'ोगे' : 'ओगे'
    if (n === 'eka') return c ? (fem ? 'ेगी' : 'ेगा') : (fem ? 'एगी' : 'एगा')

    return c ? (fem ? 'ेंगी' : 'ेंगे') : (fem ? 'एँगी' : 'एँगे')
}

const hiCommand = (kind: StemKind, p: Person, n: Num): string => {
    const c = kind === 'consonant'

    if (p === 'uttam') return n === 'eka' ? (c ? 'ूँ' : 'ऊँ') : (c ? 'ें' : 'एँ')
    if (p === 'madhyam') return c ? 'ो' : 'ओ'

    return n === 'eka' ? (c ? 'े' : 'ए') : (c ? 'ें' : 'एँ')
}

const asMeaning = (tense: Tense, p: Person, n: Num, g: SubjectGender): Lang | null => {
    const fem = p === 'anya' && g === 'stri'
    const subject = capitalise(enSubject(p, n, g))

    if (tense === 'vartaman') {
        const hiBe = p === 'uttam' ? (n === 'eka' ? 'हूँ' : 'हैं') : p === 'madhyam' ? 'हो' : n === 'eka' ? 'है' : 'हैं'

        return { hi: `${HI_SUBJECT[p][n]} ${hiBe}।`, en: `${subject} ${EN_BE[p][n]}.` }
    }

    if (tense === 'bhoot') {
        const hiWas = n === 'bahu' || p === 'madhyam' ? (fem ? 'थीं' : 'थे') : fem ? 'थी' : 'था'
        const enWas = n === 'eka' && p !== 'madhyam' ? 'was' : 'were'

        return { hi: `${HI_SUBJECT[p][n]} ${hiWas}।`, en: `${subject} ${enWas}.` }
    }

    return null
}

/**
 * "मैं पढ़ता हूँ। · I read." for one cell. Null when the meaning cannot be phrased correctly from the dictionary
 * entry (then the page shows a description instead of a translation).
 */
export const sentenceMeaning = (root: LabRoot, tense: Tense, p: Person, n: Num, g: SubjectGender = 'pu'): Lang | null => {
    if (root.group === 'as') return asMeaning(tense, p, n, g)

    const m = parse(root)

    if (!m) return null

    const fem = p === 'anya' && g === 'stri'
    const hiSubject = HI_SUBJECT[p][n]
    const subject = enSubject(p, n, g)

    if (tense === 'vartaman') {
        const verb = m.verb === 'be' ? EN_BE[p][n] : p === 'anya' && n === 'eka' ? thirdPerson(m.verb) : m.verb

        return { hi: `${hiSubject} ${m.stem}${hiPresent(p, n, fem)}।`, en: `${capitalise(subject)} ${verb}${m.tail}.` }
    }

    if (!m.kind) return null

    if (tense === 'bhavishya') {
        return { hi: `${hiSubject} ${m.stem}${hiFuture(m.kind, p, n, fem)}।`, en: `${capitalise(subject)} will ${m.verb}${m.tail}.` }
    }

    if (tense === 'vidhi') {
        const hi = `${hiSubject} ${m.stem}${hiCommand(m.kind, p, n)}।`

        if (p === 'uttam') return { hi, en: `Let ${n === 'eka' ? 'me' : 'us'} ${m.verb}${m.tail}.` }
        if (p === 'madhyam') return { hi, en: `(${capitalise(subject)}) ${m.verb}${m.tail}!` }

        return { hi, en: `Let ${enObject(n, g)} ${m.verb}${m.tail}.` }
    }

    return null
}

/** "तुम मत पढ़ो। · Do not read!" for मा + the command form (मध्यम पुरुष only). */
export const forbidMeaning = (root: LabRoot, n: Num): Lang | null => {
    const m = root.group === 'as' ? null : parse(root)

    if (!m || !m.kind) return null

    return { hi: `${HI_SUBJECT.madhyam[n]} मत ${m.stem}${hiCommand(m.kind, 'madhyam', n)}।`, en: `Do not ${m.verb}${m.tail}!` }
}

/** The plain description shown when there is no translation: "मैं · पढ़ना · भूत काल". */
export const cellGloss = (root: LabRoot, tense: Tense, p: Person, n: Num, g: SubjectGender = 'pu'): Lang => ({
    hi: `${HI_SUBJECT[p][n]} · ${root.hi} · ${TENSE_LABEL[tense].hi}`,
    en: `${capitalise(enSubject(p, n, g))} · ${root.en} · ${TENSE_LABEL[tense].en}`
})
